import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, Star, Shield, Clock, Award, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';


const whyChooseUs = [
  {
    icon: Shield,
    title: 'Premium Quality',
    description: 'We use only the finest products and techniques to ensure your vehicle receives the best care possible.',
  },
  {
    icon: Clock,
    title: 'Timely Service',
    description: 'We value your time. Our efficient processes ensure quick turnaround without compromising quality.',
  },
  {
    icon: Award,
    title: 'Expert Team',
    description: 'Our skilled technicians bring years of experience and passion to every project.',
  },
  {
    icon: Star,
    title: 'Attention to Detail',
    description: 'No detail is too small. We obsess over perfection in every vehicle we touch.',
  },
];

const testimonials = [
  {
    name: 'Rahul Sharma',
    role: 'Business Owner',
    content: 'Studio 44 transformed my Fortuner into a head-turner. The Legender conversion is flawless!',
    rating: 5,
  },
  {
    name: 'Priya Reddy',
    role: 'Car Enthusiast',
    content: 'The ceramic coating service is exceptional. My car still looks brand new after 6 months!',
    rating: 5,
  },
  {
    name: 'Arun Kumar',
    role: 'IT Professional',
    content: 'Professional team, excellent service. The interior detailing exceeded my expectations.',
    rating: 5,
  },
];

const featuredWork = [
  {
    title: 'Fortuner Transformation',
    image: '/images/gallery/2-after.png',
    category: 'Customization',
  },
  {
    title: 'Ceramic Coating',
    image: '/images/gallery/1-after.png',
    category: 'Detailing',
  },
  {
    title: 'Interior Restoration',
    image: '/images/gallery/3-after.png',
    category: 'Interior',
  },
  {
    title: 'Paint Protection',
    image: '/images/service-ppf-v2.png',
    category: 'Detailing',
  },
];

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <Hero />

      {/* Quick Highlights Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <p className="font-display text-4xl font-bold text-primary">500+</p>
              <p className="text-gray-400 mt-2">Vehicles Transformed</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <p className="font-display text-4xl font-bold text-primary">50+</p>
              <p className="text-gray-400 mt-2">Custom Builds</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <p className="font-display text-4xl font-bold text-primary">100%</p>
              <p className="text-gray-400 mt-2">Satisfaction Rate</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Services Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              What We Do
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-4">
              Our Premium Services
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { title: 'Premium Detailing', img: '/images/gallery/1-after.png' },
              { title: 'Body Kits & Customization', img: '/images/gallery/2-after.png' },
              { title: 'Paint Protection', img: '/images/service-ppf-v2.png' },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group glass-card overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-white font-semibold text-lg">{service.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="btn-outline">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              Why Studio 44
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
              Why Choose Us
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              We bring passion, precision, and professionalism to every vehicle we touch
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 text-center hover-lift group"
              >
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Work Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
                Portfolio
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
                Featured Work
              </h2>
            </div>
            <Link to="/gallery" className="btn-outline mt-6 md:mt-0">
              View Gallery
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredWork.map((work, index) => (
              <motion.div
                key={work.title}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative aspect-square rounded-2xl overflow-hidden image-zoom"
              >
                <img
                  src={work.image}
                  alt={work.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-primary text-xs uppercase tracking-wider">{work.category}</span>
                  <h3 className="text-white font-semibold text-lg mt-1">{work.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-black relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              Testimonials
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
              What Clients Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="glass-card p-8 relative"
              >
                <Quote className="w-10 h-10 text-primary/20 absolute top-6 right-6" />
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6">"{testimonial.content}"</p>
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-gray-500 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#050505]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Vehicle?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Book your consultation today and experience the Studio 44 difference.
              Premium automotive care delivered with precision and passion.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Book Now
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <a href="tel:+918179778159" className="btn-outline">
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
